import { useCallback, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  Modal,
  Linking,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  Lightbulb,
  AlertCircle,
  DollarSign,
  Target,
  MapPin,
  TrendingUp,
  TrendingDown,
  Minus,
  Microscope,
  Pill,
} from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import {
  getBlock,
  type ContentBlock,
  type ContentBlockId,
} from "@src/packages/content";
import { useScrollHaptics, springs, IconButton } from "@louise/moon-ui";
import { useLocale, useTranslations } from "@src/packages/i18n";
import InterestButton from "./InterestButton";
import DetailSectionTitle from "./DetailSectionTitle";
import FactSheetHeader, { type FactSheetTagItem } from "./FactSheetHeader";
import LinkedFactSheetText, {
  type FactSheetLinkTarget,
} from "./LinkedFactSheetText";
import THERAPY_ILLUSTRATIONS from "../../assets/images/therapies";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import SupplementDetailSheet from "./SupplementDetailSheet";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors, withAlpha } from "@louise/theme/tokens";
const supplementBottle1 =
  require("../../assets/images/supplement-bottle.png") as number;
const supplementBottle2 =
  require("../../assets/images/supplement-bottle-2.png") as number;
const supplementBottle3 =
  require("../../assets/images/supplement-bottle-3.png") as number;

const SUPPLEMENT_BOTTLES = [
  supplementBottle1,
  supplementBottle2,
  supplementBottle3,
];

function bottleIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % SUPPLEMENT_BOTTLES.length;
}

/* ─── Category display ───────────────────────────────────────────── */

const CATEGORY_COLOR: Record<string, string> = {
  "mind-body": EMBER.colors.teal,
  traditional: EMBER.colors.teal,
  physical: EMBER.colors.teal,
  nutritional: EMBER.colors.teal,
  lifestyle: EMBER.colors.teal,
  clinical: EMBER.colors.warm,
};

const CATEGORY_I18N_KEY: Record<string, string> = {
  "mind-body": "mind_body",
  traditional: "traditional",
  physical: "physical",
  nutritional: "nutritional",
  lifestyle: "lifestyle",
  clinical: "clinical",
};

function formatTherapyCost(amount: number, currency: string | undefined) {
  switch (currency) {
    case "EUR":
      return `${amount} €`;
    case "GBP":
      return `£${amount}`;
    case "USD":
    default:
      return `$${amount}`;
  }
}

function TherapyExpectationRows({
  items,
  tone,
  currentBlockId,
  onOpenFactSheet,
}: {
  items: string[];
  tone: "positive" | "caution";
  currentBlockId: ContentBlockId;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  if (!items.length) return null;

  const Icon = tone === "positive" ? CheckCircle : XCircle;
  const iconColor =
    tone === "positive"
      ? semanticColors.status.success
      : semanticColors.status.danger;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((text, index) => (
        <View
          key={`${tone}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: semanticColors.border.subtle,
            backgroundColor: semanticColors.surface.glass,
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                tone === "positive"
                  ? semanticColors.status.successTint
                  : semanticColors.status.dangerTint,
              marginTop: 1,
            }}
          >
            <Icon size={13} color={iconColor} strokeWidth={2} />
          </View>
          <LinkedFactSheetText
            text={text}
            currentBlockId={currentBlockId}
            onOpenLink={onOpenFactSheet}
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          />
        </View>
      ))}
    </View>
  );
}

function TherapyPlainRows({
  items,
  currentBlockId,
  onOpenFactSheet,
}: {
  items: string[];
  currentBlockId: ContentBlockId;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((text, index) => (
        <View
          key={`consideration-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: semanticColors.border.subtle,
            backgroundColor: semanticColors.surface.glass,
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: withAlpha(semanticColors.text.secondary, 0.1),
              marginTop: 1,
            }}
          >
            <AlertCircle
              size={13}
              color={EMBER.colors.textMid}
              strokeWidth={2}
            />
          </View>
          <LinkedFactSheetText
            text={text}
            currentBlockId={currentBlockId}
            onOpenLink={onOpenFactSheet}
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          />
        </View>
      ))}
    </View>
  );
}

/* ─── Detail Content ─────────────────────────────────────────────── */

type Props = {
  item: ContentBlock<"therapy"> | null;
  onClose: () => void;
  onFindProvider?: () => void;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
};

export function TherapyDetailContent({
  item,
  onClose,
  onFindProvider,
  skipExitAnimation,
  onOpenFactSheet,
}: {
  item: ContentBlock<"therapy">;
  onClose: () => void;
  onFindProvider?: () => void;
  skipExitAnimation?: boolean;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TherapyDetailContent");
  const { locale } = useLocale();
  const insets = useSafeAreaInsets();
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const [selectedSupplement, setSelectedSupplement] =
    useState<ContentBlock<"supplement"> | null>(null);
  const onScrollHaptics = useScrollHaptics();

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
    },
    [onScrollHaptics],
  );

  const body = locale === "fr" && item.bodyFr ? item.bodyFr : item.body;
  const sourcedFindings =
    body.keyFindings?.filter((f) => f.source && f.sourceUrl) ?? [];
  const categoryColor = CATEGORY_COLOR[body.category] ?? EMBER.colors.teal;
  const categoryKey = CATEGORY_I18N_KEY[body.category] ?? "lifestyle";
  const categoryLabel = t(`access.therapies.category.${categoryKey}`);
  const illustration = THERAPY_ILLUSTRATIONS[item.id];
  const headerTags: FactSheetTagItem[] = [
    { kind: "category", label: categoryLabel },
    {
      kind: "evidence",
      label: t(`access.therapies.evidence_label.${body.evidenceStrength}`),
      strength: body.evidenceStrength,
    },
    { kind: "audience", audience: body.forWhom },
  ];
  const needsProvider =
    body.category === "traditional" || body.category === "physical";

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor={semanticColors.surface.muted}
                  pressedBackgroundColor={semanticColors.surface.muted}
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              <FactSheetHeader
                title={body.name}
                imageSource={illustration}
                imageSize={72}
                visual={
                  illustration ? undefined : (
                    <View
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 20,
                        backgroundColor: withAlpha(categoryColor, 0.08),
                      }}
                    />
                  )
                }
                tags={headerTags}
              />

              {/* Description */}
              <LinkedFactSheetText
                text={body.description}
                currentBlockId={item.id}
                onOpenLink={onOpenFactSheet}
                style={{
                  fontSize: 14,
                  color: EMBER.colors.textMid,
                  lineHeight: 21,
                  marginTop: 18,
                  marginBottom: 4,
                }}
              />

              {/* How it works */}
              <View style={{ marginTop: 24 }}>
                <DetailSectionTitle icon={Lightbulb}>
                  {t("access.therapies.detail.section_how_it_works")}
                </DetailSectionTitle>
                <LinkedFactSheetText
                  text={body.howItWorks}
                  currentBlockId={item.id}
                  onOpenLink={onOpenFactSheet}
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textMid,
                    lineHeight: 21,
                  }}
                />
              </View>

              {/* Key findings — only show entries with both source AND sourceUrl */}
              {sourcedFindings.length > 0 && (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={Microscope}>
                    {t("access.therapies.detail.section_research")}
                  </DetailSectionTitle>
                  <View style={{ gap: 14 }}>
                    {sourcedFindings.map((finding, i) => {
                      const dirIcon =
                        finding.direction === "positive"
                          ? TrendingUp
                          : finding.direction === "negative"
                            ? TrendingDown
                            : Minus;
                      const dirColor =
                        finding.direction === "positive"
                          ? semanticColors.status.success
                          : finding.direction === "negative"
                            ? semanticColors.status.danger
                            : EMBER.colors.textLight;
                      const DirIcon = dirIcon;

                      return (
                        <View
                          key={i}
                          style={{
                            backgroundColor: semanticColors.surface.muted,
                            borderRadius: 14,
                            padding: 14,
                            borderWidth: 1,
                            borderColor: semanticColors.border.subtle,
                          }}
                        >
                          {/* Direction + outcome */}
                          <View
                            style={{
                              flexDirection: "row",
                              gap: 10,
                            }}
                          >
                            <View
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 12,
                                backgroundColor: withAlpha(dirColor, 0.08),
                                alignItems: "center",
                                justifyContent: "center",
                                marginTop: 1,
                              }}
                            >
                              <DirIcon
                                size={12}
                                color={dirColor}
                                strokeWidth={2.5}
                              />
                            </View>
                            <LinkedFactSheetText
                              text={finding.outcome}
                              currentBlockId={item.id}
                              onOpenLink={onOpenFactSheet}
                              style={{
                                flex: 1,
                                fontSize: 14,
                                fontWeight: "600",
                                color: EMBER.colors.text,
                                lineHeight: 20,
                              }}
                            />
                          </View>

                          {/* Magnitude */}
                          {finding.magnitude && (
                            <View
                              style={{
                                marginTop: 8,
                                marginLeft: 34,
                                paddingHorizontal: 10,
                                paddingVertical: 5,
                                borderRadius: 8,
                                backgroundColor: withAlpha(dirColor, 0.04),
                                alignSelf: "flex-start",
                              }}
                            >
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 12,
                                  fontWeight: "700",
                                  color: dirColor,
                                  letterSpacing: 0.2,
                                }}
                              >
                                {finding.magnitude}
                              </Text>
                            </View>
                          )}

                          {/* Mechanism */}
                          {finding.mechanism && (
                            <LinkedFactSheetText
                              text={finding.mechanism}
                              currentBlockId={item.id}
                              onOpenLink={onOpenFactSheet}
                              style={{
                                marginTop: 8,
                                marginLeft: 34,
                                fontSize: 13,
                                color: EMBER.colors.textLight,
                                lineHeight: 19,
                                fontStyle: "italic",
                              }}
                            />
                          )}

                          {/* Source */}
                          {finding.source &&
                            (finding.sourceUrl ? (
                              <Pressable
                                onPress={() => {
                                  capturePress(
                                    pressLabel(finding.source),
                                    "open_therapy_source",
                                  );
                                  void Linking.openURL(finding.sourceUrl!);
                                }}
                                style={{
                                  marginTop: 6,
                                  marginLeft: 34,
                                }}
                              >
                                <Text
                                  className="font-dm-sans"
                                  style={{
                                    fontSize: 11,
                                    color: EMBER.colors.teal,
                                    lineHeight: 16,
                                    textDecorationLine: "underline",
                                  }}
                                >
                                  {finding.source}
                                </Text>
                              </Pressable>
                            ) : (
                              <Text
                                className="font-dm-sans"
                                style={{
                                  marginTop: 6,
                                  marginLeft: 34,
                                  fontSize: 11,
                                  color: EMBER.colors.textFaint,
                                  lineHeight: 16,
                                }}
                              >
                                {finding.source}
                              </Text>
                            ))}
                        </View>
                      );
                    })}
                  </View>
                </View>
              )}

              {/* Practical details */}
              {(body.sessionDuration ||
                body.frequencyRange ||
                body.costRange) && (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={Clock}>
                    {t("access.therapies.detail.section_practical")}
                  </DetailSectionTitle>
                  <View style={{ gap: 6 }}>
                    {body.sessionDuration && (
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 14, color: EMBER.colors.textMid }}
                      >
                        {t("access.therapies.detail.session_label", {
                          params: { value: body.sessionDuration },
                        })}
                      </Text>
                    )}
                    {body.frequencyRange && (
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 14, color: EMBER.colors.textMid }}
                      >
                        {t("access.therapies.detail.frequency_label", {
                          params: { value: body.frequencyRange },
                        })}
                      </Text>
                    )}
                    {body.costRange && (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <DollarSign
                          size={12}
                          color={EMBER.colors.textFaint}
                          strokeWidth={1.5}
                        />
                        <Text
                          className="font-dm-sans"
                          style={{ fontSize: 14, color: EMBER.colors.textMid }}
                        >
                          {t("access.therapies.detail.cost_per_session", {
                            params: {
                              low: formatTherapyCost(
                                body.costRange.low,
                                body.currency,
                              ),
                              high: formatTherapyCost(
                                body.costRange.high,
                                body.currency,
                              ),
                            },
                          })}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              )}

              {/* What to expect */}
              {body.expectations && (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={Lightbulb}>
                    {t("access.therapies.detail.section_what_to_expect")}
                  </DetailSectionTitle>
                  <TherapyExpectationRows
                    items={body.expectations.canExpect}
                    tone="positive"
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </View>
              )}

              {/* Keep in mind */}
              {body.expectations?.shouldNotExpect?.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={AlertCircle}>
                    {t("access.therapies.detail.section_keep_in_mind")}
                  </DetailSectionTitle>
                  <TherapyExpectationRows
                    items={body.expectations.shouldNotExpect}
                    tone="caution"
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </View>
              ) : null}

              {/* Considerations */}
              {body.considerations?.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={AlertCircle}>
                    {t("access.therapies.detail.section_considerations")}
                  </DetailSectionTitle>
                  <TherapyPlainRows
                    items={body.considerations}
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </View>
              ) : null}

              {/* Best for */}
              {body.bestFor?.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={Target}>
                    {t("access.therapies.detail.section_best_for")}
                  </DetailSectionTitle>
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    {body.bestFor.map((tag) => (
                      <View
                        key={tag}
                        style={{
                          paddingHorizontal: 10,
                          paddingVertical: 5,
                          borderRadius: 8,
                          backgroundColor: semanticColors.surface.muted,
                          borderWidth: 1,
                          borderColor: semanticColors.border.subtle,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 11,
                            fontWeight: "500",
                            color: EMBER.colors.textMid,
                            letterSpacing: 0.2,
                          }}
                        >
                          {tag}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              ) : null}

              {/* Supporting supplements */}
              {body.relatedSupplements?.ids.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={Pill}>
                    {t(
                      "access.therapies.detail.section_supporting_supplements",
                    )}
                  </DetailSectionTitle>
                  {body.relatedSupplements.patternNote ? (
                    <LinkedFactSheetText
                      text={body.relatedSupplements.patternNote}
                      currentBlockId={item.id}
                      onOpenLink={onOpenFactSheet}
                      style={{
                        fontSize: 13,
                        color: EMBER.colors.textLight,
                        lineHeight: 20,
                        marginBottom: 14,
                      }}
                    />
                  ) : null}
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {body.relatedSupplements.ids.map((id) => {
                      const supplement = getBlock(id as ContentBlockId);
                      if (!supplement) return null;
                      return (
                        <Pressable
                          key={id}
                          onPress={() => {
                            capturePress(
                              pressLabel(supplement.title),
                              "open_related_supplement",
                            );
                            setSelectedSupplement(
                              supplement as ContentBlock<"supplement">,
                            );
                          }}
                          style={{
                            width: "31%",
                            borderRadius: 16,
                            overflow: "hidden",
                            backgroundColor:
                              semanticColors.action.quiet.background,
                            borderWidth: 1,
                            borderColor: semanticColors.border.subtle,
                            alignItems: "center",
                            paddingTop: 12,
                            paddingBottom: 10,
                            paddingHorizontal: 6,
                          }}
                        >
                          <Image
                            source={SUPPLEMENT_BOTTLES[bottleIndex(id)]}
                            style={{ width: 36, height: 46 }}
                            resizeMode="contain"
                          />
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 10,
                              fontWeight: "600",
                              color: EMBER.colors.text,
                              textAlign: "center",
                              marginTop: 6,
                              lineHeight: 13,
                            }}
                            numberOfLines={2}
                          >
                            {supplement.title}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ) : null}

              {/* Find a provider / Interest CTA */}
              <View style={{ marginTop: 24, gap: 12 }}>
                {needsProvider && onFindProvider && (
                  <Pressable
                    onPress={() => {
                      capturePress(
                        pressLabel(
                          t("access.therapies.detail.find_provider_cta"),
                        ),
                        "access.therapies.detail.find_provider_cta",
                      );
                      onFindProvider();
                    }}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      paddingVertical: 14,
                      paddingHorizontal: 24,
                      borderRadius: 12,
                      backgroundColor: withAlpha(EMBER.colors.teal, 0.08),
                      borderWidth: 1,
                      borderColor: withAlpha(EMBER.colors.teal, 0.19),
                    }}
                  >
                    <MapPin
                      size={16}
                      color={EMBER.colors.teal}
                      strokeWidth={2}
                    />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 15,
                        fontWeight: "600",
                        color: EMBER.colors.teal,
                      }}
                    >
                      {t("access.therapies.detail.find_provider_cta")}
                    </Text>
                  </Pressable>
                )}
                <InterestButton
                  featureId={item.id}
                  featureType="therapy"
                  featureName={item.body.name}
                />
              </View>
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>

      <SupplementDetailSheet
        item={selectedSupplement}
        onClose={() => setSelectedSupplement(null)}
      />
    </View>
  );
}

export default function TherapyDetailSheet({
  item,
  onClose,
  onFindProvider,
  onOpenFactSheet,
}: Props) {
  if (!item) return null;
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <TherapyDetailContent
        item={item}
        onClose={onClose}
        onFindProvider={onFindProvider}
        onOpenFactSheet={onOpenFactSheet}
      />
    </Modal>
  );
}
